import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, STATUS_OPTIONS } from "@/lib/rn3";
import { toast } from "sonner";
import { Users, FileText, Calendar, Upload, Sparkles, TrendingUp, Briefcase, ListTodo, MessageSquare, Mail, Gift, DollarSign, Shield } from "lucide-react";
import { ClientDetailDrawer, ClientStatusPill } from "@/components/admin/ClientDetailDrawer";
import SessionCard from "@/components/SessionCard";
import ScanStatusPanel from "@/components/admin/ScanStatusPanel";
import StatementDigestCard from "@/components/admin/StatementDigestCard";
import ResetDemoDataCard from "@/components/admin/ResetDemoDataCard";
import NewBadge from "@/components/NewBadge";
import DocumentEditModal from "@/components/admin/DocumentEditModal";
import { usePolling } from "@/lib/usePolling";
import { useT, useLang } from "@/lib/i18n";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <Icon className="w-6 h-6 text-[#C4A252]" />
    <div className="mt-3 text-3xl font-serif-display text-[#0A0A0A]">{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const Admin = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const [stats, setStats] = useState(null);
  const [intakes, setIntakes] = useState([]);
  const [appts, setAppts] = useState([]);
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [replyDrafts, setReplyDrafts] = useState({}); // {clientUserId: text}
  const [activeClientId, setActiveClientId] = useState(null);
  const [editDoc, setEditDoc] = useState(null); // document being amended
  const [taskStats, setTaskStats] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/portal");
  }, [user, loading, nav]);

  const load = async () => {
    try {
      const [s, i, a, c, d, ts, m] = await Promise.all([
        api.get("/admin/stats"), api.get("/intakes"), api.get("/appointments"),
        api.get("/admin/clients"), api.get("/documents"),
        api.get("/admin/tasks/stats"), api.get("/messages"),
      ]);
      setStats(s.data); setIntakes(i.data); setAppts(a.data); setClients(c.data); setDocs(d.data);
      setTaskStats(ts.data); setMessages(m.data);
    } catch { /* noop */ }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  // Auto-refresh dashboard data every 15s while the tab is visible so new
  // intakes, documents, and messages appear without a manual reload. Paused
  // when the tab is hidden; resumes immediately on focus.
  usePolling(() => { if (user?.role === "admin") load(); }, 15000);

  // Reply to a client message — POSTs with target_user_id so the backend
  // fires the instant Resend email to that client.
  const sendReply = async (clientUserId) => {
    const body = (replyDrafts[clientUserId] || "").trim();
    if (!body) return;
    try {
      await api.post("/messages", { body, target_user_id: clientUserId, to_admin: false });
      setReplyDrafts((d) => ({ ...d, [clientUserId]: "" }));
      toast.success(t("admin.messages.reply_sent") || "Reply sent");
      load();
    } catch {
      toast.error(t("admin.toast.failed"));
    }
  };

  // Authenticated document download — the GET /documents/{id}/download endpoint
  // requires a JWT in the Authorization header, which a plain <a target="_blank">
  // can't supply, so we fetch via the axios client (which carries the JWT) and
  // trigger a download from a blob URL.
  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(t("admin.toast.failed"));
    }
  };

  const updateIntakeStatus = async (id, status) => {
    try {
      await api.patch(`/intakes/${id}/status`, { status });
      toast.success(t("admin.toast.status_updated"));
      load();
    } catch { toast.error(t("admin.toast.failed")); }
  };
  const updateAppt = async (id, payload) => {
    try {
      await api.patch(`/appointments/${id}`, payload);
      toast.success(t("admin.toast.updated"));
      load();
    } catch { toast.error(t("admin.toast.failed")); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="rn3-container py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="rn3-eyebrow">{t("admin.eyebrow")}</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl mt-2 break-words">{t("admin.welcome")}, {user.full_name?.split(" ")[0] || "Adelis"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          <Link to="/admin/revenue" className="rn3-btn-primary text-xs sm:text-sm" data-testid="admin-revenue-link">
            <DollarSign className="w-4 h-4" /> {t("admin.nav.revenue")}
          </Link>
          <Link to="/admin/tasks" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-tasks-link">
            <ListTodo className="w-4 h-4" /> {t("admin.nav.tasks")}
          </Link>
          <Link to="/admin/sms" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-sms-link">
            <MessageSquare className="w-4 h-4" /> {t("admin.nav.sms")}
          </Link>
          <Link to="/admin/email" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-email-link">
            <Mail className="w-4 h-4" /> {t("admin.nav.email")}
          </Link>
          <Link to="/admin/referrals" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-referrals-link">
            <Gift className="w-4 h-4" /> {t("admin.nav.referrals")}
          </Link>
          <Link to="/admin/leads" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-leads-link">
            <TrendingUp className="w-4 h-4" /> {t("admin.nav.leads")}
          </Link>
          <Link to="/admin/activity" className="rn3-btn-gold text-xs sm:text-sm" data-testid="admin-activity-link">
            <Shield className="w-4 h-4" /> {t("admin.nav.activity")}
          </Link>
          <Link to="/back-office" className="rn3-btn-primary text-xs sm:text-sm" data-testid="admin-bk-link">
            <Sparkles className="w-4 h-4" /> {t("admin.nav.back_office")}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4 mt-10">
        <Stat icon={Users} label={t("admin.stats.clients")} value={stats?.clients ?? "—"} testid="stat-clients" />
        <Stat icon={Briefcase} label={t("admin.stats.books")} value={stats?.books ?? "—"} testid="stat-books" />
        <Link to="/admin/leads"><Stat icon={TrendingUp} label={t("admin.stats.new_leads")} value={stats?.new_leads ?? "—"} testid="stat-leads" /></Link>
        <Stat icon={FileText} label={t("admin.stats.intakes")} value={stats?.intakes ?? "—"} testid="stat-intakes" />
        <Stat icon={Calendar} label={t("admin.stats.upcoming_appts")} value={stats?.upcoming_appointments ?? "—"} testid="stat-appts" />
        <Link to="/admin/tasks">
          <div className="rn3-card" data-testid="stat-tasks">
            <ListTodo className="w-6 h-6 text-[#C4A252]" />
            <div className="mt-3 text-3xl font-serif-display text-[#0A0A0A]">{(taskStats?.pending ?? 0) + (taskStats?.in_progress ?? 0)}</div>
            <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{t("admin.stats.open_tasks")}</div>
            {taskStats?.overdue > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-[#8B1C22] mt-1">{taskStats.overdue} {t("admin.stats.overdue")}</div>
            )}
          </div>
        </Link>
      </div>

      {/* Intakes */}
      <section className="mt-12">
        <h2 className="font-serif-display text-2xl">{t("admin.intakes.heading")}</h2>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
        <div className="overflow-x-auto" data-testid="intakes-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr>
                <th className="py-3 pr-4">{t("admin.col.date")}</th>
                <th className="py-3 pr-4">{t("admin.col.client")}</th>
                <th className="py-3 pr-4">{t("admin.col.service")}</th>
                <th className="py-3 pr-4">{t("admin.col.urgency")}</th>
                <th className="py-3 pr-4">{t("admin.col.status")}</th>
              </tr>
            </thead>
            <tbody>
              {intakes.length === 0 && <tr><td colSpan="5" className="py-6 text-[#737373]">{t("admin.intakes.empty")}</td></tr>}
              {intakes.map((i) => (
                <tr key={i.id} className="border-b border-[#F3EFE9]" data-testid={`admin-intake-${i.id}`}>
                  <td className="py-3 pr-4 whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</td>
                  <td className="py-3 pr-4"><div className="flex items-center">{i.full_name}<NewBadge createdAt={i.created_at} /></div><div className="text-xs text-[#737373]">{i.email} · {i.phone}</div></td>
                  <td className="py-3 pr-4">{i.service.replace(/_/g, " ")}</td>
                  <td className="py-3 pr-4 capitalize">{i.urgency}</td>
                  <td className="py-3 pr-4">
                    <select value={i.status} onChange={(e) => updateIntakeStatus(i.id, e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs" data-testid={`status-select-${i.id}`}>
                      {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Appointments */}
      <section className="mt-12">
        <h2 className="font-serif-display text-2xl">{t("admin.appts.heading")}</h2>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
        <div className="overflow-x-auto" data-testid="appts-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr>
                <th className="py-3 pr-4">{t("admin.col.date")}</th>
                <th className="py-3 pr-4">{t("admin.col.time")}</th>
                <th className="py-3 pr-4">{t("admin.col.client")}</th>
                <th className="py-3 pr-4">{t("admin.col.service")}</th>
                <th className="py-3 pr-4">{t("admin.col.status")}</th>
                <th className="py-3 pr-4">{t("admin.col.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {appts.length === 0 && <tr><td colSpan="6" className="py-6 text-[#737373]">{t("admin.appts.empty")}</td></tr>}
              {appts.map((a) => (
                <tr key={a.id} className="border-b border-[#F3EFE9]">
                  <td className="py-3 pr-4">{a.date}</td>
                  <td className="py-3 pr-4">{a.time}</td>
                  <td className="py-3 pr-4">{a.full_name}<div className="text-xs text-[#737373]">{a.email}</div></td>
                  <td className="py-3 pr-4">{a.service.replace(/_/g, " ")}</td>
                  <td className="py-3 pr-4">
                    <select value={a.status} onChange={(e) => updateAppt(a.id, { status: e.target.value })} className="border border-[#E5E5E5] px-2 py-1 text-xs" data-testid={`appt-status-${a.id}`}>
                      {["pending","approved","rescheduled","cancelled","completed"].map((s) => <option key={s} value={s}>{t(`admin.appt_status.${s}`)}</option>)}
                    </select>
                  </td>
                  <td className="py-3 pr-4 text-xs">
                    <button onClick={() => updateAppt(a.id, { status: "approved" })} className="text-[#8B1C22] mr-2">{t("admin.appts.approve")}</button>
                    <button onClick={() => updateAppt(a.id, { status: "cancelled" })} className="text-[#737373]">{t("admin.appts.cancel")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Clients */}
      <section className="mt-12">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-serif-display text-2xl">{t("admin.clients.heading")}</h2>
            <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
          </div>
          <div className="text-xs text-[#737373] mb-5">{t("admin.clients.hint")}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
              <tr>
                <th className="py-3 pr-4">{t("admin.col.name")}</th>
                <th className="py-3 pr-4">{t("admin.col.email")}</th>
                <th className="py-3 pr-4">{t("admin.col.phone")}</th>
                <th className="py-3 pr-4">{t("admin.col.lang")}</th>
                <th className="py-3 pr-4">{t("admin.col.status")}</th>
                <th className="py-3 pr-4">{t("admin.col.joined")}</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setActiveClientId(c.id)}
                  className="border-b border-[#F3EFE9] cursor-pointer hover:bg-[#FDFBF7] transition"
                  data-testid={`client-row-${c.id}`}
                >
                  <td className="py-3 pr-4 font-medium">{c.full_name}</td>
                  <td className="py-3 pr-4">{c.email}</td>
                  <td className="py-3 pr-4">{c.phone}</td>
                  <td className="py-3 pr-4 uppercase">{c.preferred_language}</td>
                  <td className="py-3 pr-4"><ClientStatusPill status={c.internal_status} /></td>
                  <td className="py-3 pr-4">{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-sm text-[#737373]">{t("admin.clients.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent docs */}
      <section className="mt-12">
        <h2 className="font-serif-display text-2xl">{t("admin.documents.heading")}</h2>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.slice(0, 12).map((d) => {
            const t18n = (k, fb) => t(k) === k ? fb : t(k);
            const catDisplay = d.category_key === "other"
              ? (d.category_other || t18n("portal.upload.cat.other", "Other"))
              : (d.category_key ? t18n(`portal.upload.cat.${d.category_key}`, d.category_key.replace(/_/g, " ")) : null);
            return (
              <div key={d.id} className="border border-[#E5E5E5] p-4 text-sm space-y-1" data-testid={`admin-doc-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="truncate font-medium" title={d.filename}>{d.filename}</div>
                  <NewBadge createdAt={d.created_at} />
                </div>
                {d.user_name && <div className="text-xs text-[#0A0A0A] font-medium">{d.user_name}</div>}
                <div className="text-xs text-[#737373]">{d.user_email}</div>

                {/* Structured metadata badges — only rendered when populated */}
                <div className="flex flex-wrap gap-1 pt-1">
                  {d.doc_type && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#8B1C22] text-white">
                      {t18n(`portal.upload.type.${d.doc_type}`, d.doc_type.replace(/_/g, " "))}
                    </span>
                  )}
                  {catDisplay && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#FDFBF7] border border-[#C4A252] text-[#8B1C22]">
                      {catDisplay}{d.subcategory ? ` › ${d.subcategory}` : ""}
                    </span>
                  )}
                  {d.period && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#F3EFE9] text-[#0A0A0A]">
                      {d.period}
                    </span>
                  )}
                </div>

                {(d.account_label || d.vendor || d.amount != null) && (
                  <div className="text-xs text-[#0A0A0A] pt-1 space-y-0.5">
                    {d.account_label && <div><span className="text-[#737373]">{t18n("portal.upload.account_label", "Account")}:</span> {d.account_label}</div>}
                    {d.vendor && <div><span className="text-[#737373]">{t18n("portal.upload.vendor_label", "Vendor")}:</span> {d.vendor}</div>}
                    {d.amount != null && <div><span className="text-[#737373]">{t18n("portal.upload.amount_label", "Amount")}:</span> <b>${Number(d.amount).toFixed(2)}</b></div>}
                  </div>
                )}
                {d.notes && (
                  <div className="text-xs text-[#737373] italic border-l-2 border-[#C4A252] pl-2 pt-1">{d.notes}</div>
                )}

                <div className="text-xs text-[#737373] pt-1">{new Date(d.created_at).toLocaleString()}</div>
                <div className="flex items-center gap-3 pt-2">
                  <button onClick={() => downloadDoc(d)} className="text-[#8B1C22] text-xs hover:underline cursor-pointer" data-testid={`admin-doc-download-${d.id}`}>{t("admin.documents.download")}</button>
                  <button onClick={() => setEditDoc(d)} className="text-[#0A0A0A] text-xs hover:underline cursor-pointer" data-testid={`admin-doc-edit-${d.id}`}>{t("admin.documents.edit") || "Edit"}</button>
                </div>
              </div>
            );
          })}
          {docs.length === 0 && <div className="text-sm text-[#737373]">{t("admin.documents.empty")}</div>}
        </div>
      </section>

      {/* Client Messages — newest first; click "Reply" to send + auto-email the client */}
      <section className="mt-12" data-testid="admin-messages">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-serif-display text-2xl flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#C4A252]" />{t("admin.messages.heading") || "Client Messages"}</h2>
          <button onClick={load} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22]" data-testid="admin-messages-refresh">{t("admin.messages.refresh") || "Refresh"}</button>
        </div>
        <div className="border-t border-[#C4A252] w-10 mt-3 mb-5" />
        <div className="space-y-4">
          {messages.filter((m) => !m.from_admin).slice(0, 10).map((m) => {
            const draft = replyDrafts[m.from_user_id] || "";
            return (
              <div key={m.id} className="border border-[#E5E5E5] p-4 text-sm" data-testid={`admin-message-${m.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-medium flex items-center">{m.from_name || m.from_email}<NewBadge createdAt={m.created_at} /></div>
                    <div className="text-xs text-[#737373]">{m.from_email}</div>
                  </div>
                  <div className="text-xs text-[#737373]">{new Date(m.created_at).toLocaleString()}</div>
                </div>
                <div className="mt-3 text-[#0A0A0A] whitespace-pre-wrap border-l-4 border-[#C4A252] bg-[#FAF7F0] p-3">{m.body}</div>
                <div className="mt-3 flex gap-2">
                  <textarea
                    rows={2}
                    value={draft}
                    onChange={(e) => setReplyDrafts((d) => ({ ...d, [m.from_user_id]: e.target.value }))}
                    placeholder={t("admin.messages.reply_placeholder") || "Type your reply… the client will get an instant email."}
                    className="flex-1 border border-[#E5E5E5] p-2 text-sm focus:outline-none focus:border-[#8B1C22]"
                    data-testid={`admin-reply-input-${m.from_user_id}`}
                  />
                  <button
                    onClick={() => sendReply(m.from_user_id)}
                    disabled={!draft.trim()}
                    className="rn3-btn-primary text-xs h-fit disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`admin-reply-send-${m.from_user_id}`}
                  >
                    {t("admin.messages.send_reply") || "Send Reply"}
                  </button>
                </div>
              </div>
            );
          })}
          {messages.filter((m) => !m.from_admin).length === 0 && (
            <div className="text-sm text-[#737373]">{t("admin.messages.empty") || "No client messages yet."}</div>
          )}
        </div>
      </section>

      {/* Account Security — mirrors the client portal so the admin can also see/manage their own sessions */}
      <section className="mt-12" data-testid="admin-security">
        <div className="rn3-eyebrow flex items-center gap-2 mb-3"><Shield className="w-4 h-4" /> {t("admin.security.heading")}</div>
        <SessionCard />
      </section>

      {/* Virus scan telemetry — live ClamAV health + recent malware blocks */}
      <ScanStatusPanel />

      <StatementDigestCard lang={lang} />

      <ResetDemoDataCard lang={lang} />

      <ClientDetailDrawer
        clientId={activeClientId}
        onClose={() => setActiveClientId(null)}
        onUpdated={load}
      />
      {editDoc && (
        <DocumentEditModal
          doc={editDoc}
          onClose={() => setEditDoc(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export default Admin;
